
import AppCustomLanguageSelect from "@/components/CustomLanguageSelect.vue";
import { SaleLayoutConf } from "@/models/sale-layout";
import { SaleService } from "@/services/sale-service";
import { SaleLayout } from "@/utils/sale-layouts/sale-layout";
import Vue from "vue";
import { IBreadCrumb, ISelectItem } from "winfakt-vue-components";
import AppPage from "./Page.vue";
import AppTab from "./Tab.vue";
import AppTabs from "./Tabs.vue";
import { AuthService } from "../../services/auth-service";
import { Tier } from "../../utils/payments/tiers";
import AppEditorSettingsEdit from "./EditorSettingsEdit.vue";
import AppMissingModuleModal from "@/components/common/MissingModuleModal.vue";
import AppPageContainer from "@/components/layout-editor/PageContainer.vue";
import AppRequirementsPage from "./RequirementsPage.vue";
import { HttpError } from "@/utils/services/http-error";
import { Sale } from "@/models/base";
import { RequirementsLocation } from "@/utils/sale-layouts/requirements";
import AppLayoutHistoryModal from "@/components/sale-layout-editor/LayoutHistoryModal.vue";
import AppUploadTemplateModal from "./UploadTemplateModal.vue";

export default Vue.extend({
	data() {
		return {
			tab: null,
			zoomLevelIndex: 4,
			path: [{
				to: "/settings/sale-layouts/", value: this.$ct("layouts")
			}] as IBreadCrumb[],
			selectedLang: "nl-be",
			langModalOpen: false,
			showGrid: false,
			editorSettingsModalOpen: false,
			missingModule: false,
			historyModalOpen: false,
			exampleDocument: null as null | Sale,
			uploadTemplateModalOpen: false
		};
	},
	props: {
		layout: {
			type: Object as () => SaleLayoutConf,
			required: true
		},
	},
	computed: {
		fullControl(): Boolean {
			if(AuthService.isDevModeEnabled || AuthService.hasModule(13)){
				return true;
			}
			return false;
		},
		zoomLevels(): number[] {
			return [25, 50, 75, 85, 100, 150, 200, 400];
		},
		zoomLevel(): number {
			return this.zoomLevels[this.zoomLevelIndex];
		},
		saleLayout(): SaleLayout {
			return this.layout.Data;
		},
		devModeButtonBarItems():ISelectItem<Function>[]{
			return [{
					text: "Layout downloaden als afbeelding",
					value: this.downloadTemplate
				},
				{
					text: "Layout downloaden als json",
					value: this.downloadTemplateAsJson
				},
				{
					text: "Layout template laden uit een bestand",
					value: this.openUploadTemplateModal,
				}];
		},
		buttonBarItmes(): ISelectItem<Function>[] {
			let items:ISelectItem<Function>[] = [
				{
					text: this.$ct("change-display-language").toString(),
					value: this.openChangeLanguage
				},
				{
					text: this.$ct("download-preview").toString(),
					value: this.downloadPreview
				},
				{
					text: this.$ct("edit-editor-settings"),
					value: this.openEditorSettingsModal
				},
				{
					text: this.showGrid ?  this.$ct("hide-grid").toString() : this.$ct("show-grid").toString(),
					value: this.toggleGrid
				},
			];
			if (AuthService.wfCompany.Tier >= Tier.ENTERPRISE){
				items.push({
					text: this.$ct("duplicate-layout").toString(),
					value: this.duplicateLayout
				});
			}
			if (this.layout.ID != 0 ){
				items.push({
					text: this.$ct("layout-history").toString(),
					value: this.layoutHistory
				});
			}

			if (AuthService.isDevModeEnabled){
				items.push(...this.devModeButtonBarItems);
			}
			return items;
		},
		showRequirementsPage():boolean {
			return this.layout.Data.Requirements.Location == RequirementsLocation.NEW_PAGE;
		}
	},
	watch: {
		"layout.Data.EditorSettings.PreviewDocumentID": {
			immediate: true,
			async handler(){
				this.exampleDocument = null;
				if (this.layout.Data.EditorSettings.PreviewDocumentID != "") {
					try {
						this.exampleDocument = await SaleService.getByFriendlyId(this.layout.Data.EditorSettings.PreviewDocumentID);
					}catch(err){
						let e = err as HttpError;
						e.dontShow = true;
						this.$wf.notify(this.$ct("cant-find-example-document").toString(), "warning", 3000, "exclamation-triangle");
					}
				}
			}
		}
	},
	methods: {
		save() {
			this.$emit("save");
		},
		zoomOut() {
			if (this.zoomLevelIndex > 0) {
				this.zoomLevelIndex--;
			}
		},
		zoomIn() {
			if (this.zoomLevelIndex < this.zoomLevels.length - 1) {
				this.zoomLevelIndex++;
			}
		},
		toggleGrid(){
			this.showGrid = !this.showGrid;
		},
		openChangeLanguage() {
			this.langModalOpen = true;
		},
		closeLangModal() {
			this.langModalOpen = false;
		},
		triggerDropdown(event: any) {
			if (!event) return;
			event();
		},
		layoutHistory(){
			this.historyModalOpen = true;
		},
		onInput(layout:SaleLayout){
			this.layout.Data = layout;
		},
		async downloadPreview() {
			await SaleService.downloadSaleLayoutPreview(this.layout.Data, this.selectedLang);
		},
		async duplicateLayout() {
			if (!AuthService.hasModule(13)) {
				this.missingModule = true;
				return;
			}
			let layout = this.layout;
			layout.Name += "_copy";
			layout.ID = 0;
			let result = await SaleService.postSaleLayouts([layout]);
			if (result) {
				this.$router.push(`/settings/sale-layout/` + result[0].ID);
				this.$wf.notify(this.$ct("duplicate-succes").toString(), "success", 3000, "check");
			}
		},
		openEditorSettingsModal(){
			this.editorSettingsModalOpen = true;
		},
		setZoomLevel(){
			if(window.innerWidth>1350){
				this.zoomLevelIndex = 4;
				return;
			}
			if(window.innerWidth>1160){
				this.zoomLevelIndex = 3;
				return;
			}
			if(window.innerWidth>1069){
				this.zoomLevelIndex = 2;
				return;
			}
			if(window.innerWidth>800){
				this.zoomLevelIndex = 1;
				return;
			}
		},
		async downloadTemplate(){
			await SaleService.downloadLayoutTemplate(this.layout.Data, "jpg");
		},
		async downloadTemplateAsJson(){
			await SaleService.downloadLayoutTemplate(this.layout.Data, "json");
		},
		openUploadTemplateModal(){
			this.uploadTemplateModalOpen = true;
		}
	},
	async mounted() {
		this.path.push({to: "/settings/sale-layouts/", value: ""});
		window.addEventListener("resize", this.setZoomLevel);
		this.setZoomLevel();
	},
	destroyed() {
		window.removeEventListener("resize", this.setZoomLevel);
	},
	components: {
		AppTabs,
		AppPage,
		AppTab,
		AppCustomLanguageSelect,
		AppEditorSettingsEdit,
		AppMissingModuleModal,
		AppPageContainer,
		AppRequirementsPage,
		AppLayoutHistoryModal,
		AppUploadTemplateModal
	},
	i18nEx: {
		componentPrefix: "components.sale-layout-editor"
	}
});
